/* eslint no-console: 0 */

import includes from 'lodash/includes';
import Rollbar from 'rollbar';

import config from '../config';

import { LOG_LEVELS } from './constants/logger';

const logLevelsConfigured = LOG_LEVELS.slice(0, LOG_LEVELS.indexOf(config.logLevel) + 1);

class RollbarLogger {
  constructor(accessToken) {
    if (config.environment !== 'local') {
      this.rollbar = new Rollbar({
        accessToken,
        captureUncaught: true,
        captureUnhandledRejections: true,
        payload: {
          environment: config.environment,
          client: {
            javascript: {
              source_map_enabled: true,
              code_version: config.commitHash,
              guess_uncaught_frames: true,
            },
          },
        },
        checkIgnore: (isUncaught, args, payload) => {
          // extremely conservative rejection for only the most obvious, high volume false positives
          const isUnknown = (isUncaught
            && args === undefined
            && payload.body.trace.exception.class === '(unknown)'
            && payload.body.trace.exception.description === 'unhandled rejection was null or undefined!'
            && payload.body.trace.frames.length === 1
            && payload.body.trace.frames[0].filename === '(unknown)'
            && payload.body.trace.frames[0].method === '[anonymous]');

          const isScriptError = (isUncaught
            && payload.body.trace.exception.description === 'Script error.'
            && payload.body.trace.exception.message === 'Script error.');

          return isUnknown || isScriptError;
        },
        transform: (payload) => {
          const { trace } = payload.body;
          const locRegex = /^(https?):\/\/[a-zA-Z0-9._-]+\.jobcase\.com(.*)/;
          if (trace && trace.frames) {
            for (let i = 0; i < trace.frames.length; i += 1) {
              const { filename } = trace.frames[i];
              if (filename) {
                const m = filename.match(locRegex);
                trace.frames[i].filename = `${m[1]}://dynamichost${m[2]}`;
              }
            }
          }
        },
      });
    }
  }

  logError(message = 'ERROR', error = null) {
    if (includes(logLevelsConfigured, 'ERROR')) {
      if (this.rollbar) {
        this.rollbar.error(message, error);
      } else {
        console.error(`${message}:`, error);
      }
    }
  }

  logWarning(message = 'WARN', error = null) {
    if (includes(logLevelsConfigured, 'WARN')) {
      if (this.rollbar) {
        this.rollbar.warning(message, error);
      } else {
        console.warn(`${message}:`, error);
      }
    }
  }

  logInfo(message = 'INFO', error = null) {
    if (includes(logLevelsConfigured, 'INFO')) {
      if (this.rollbar) {
        this.rollbar.info(message, error);
      } else {
        console.log(`${message}:`, error);
      }
    }
  }

  logDebug(message = 'DEBUG', error = null) {
    if (includes(logLevelsConfigured, 'DEBUG')) {
      if (this.rollbar) {
        this.rollbar.debug(message, error);
      } else {
        console.debug(`${message}:`, error);
      }
    }
  }
}

class Logger {
  clientLogger = new RollbarLogger(config.rollbarClientToken);

  serverLogger = new RollbarLogger(config.rollbarServerToken);

  logError(message, error) {
    if (process.browser) {
      this.clientLogger.logError(message, error);
    } else {
      this.serverLogger.logError(message, error);
    }
  }

  logWarning(message = 'WARN', error = null) {
    if (process.browser) {
      this.clientLogger.logWarning(message, error);
    } else {
      this.serverLogger.logWarning(message, error);
    }
  }

  logInfo(message = 'INFO', error = null) {
    if (process.browser) {
      this.clientLogger.logInfo(message, error);
    } else {
      this.serverLogger.logInfo(message, error);
    }
  }

  logDebug(message = 'DEBUG', error = null) {
    if (process.browser) {
      this.clientLogger.logDebug(message, error);
    } else {
      this.serverLogger.logDebug(message, error);
    }
  }
}

export default new Logger();
